import { storage } from '../utils/storage';

/**
 * Minimal internationalization (Thai / English). Thai is the default.
 *
 * `t(key)` returns the string for the current language, falling back to English
 * then the key itself. Data-object names (buildings/staff/upgrades/districts)
 * are translated by id via `tName`.
 */
export type Lang = 'th' | 'en';

const LANG_KEY = 'parking-tycoon:lang';
let lang: Lang = (storage.get(LANG_KEY) as Lang) || 'th';

export function getLang(): Lang {
  return lang;
}

export function setLang(l: Lang): void {
  lang = l;
  storage.set(LANG_KEY, l);
}

export function toggleLang(): Lang {
  setLang(lang === 'th' ? 'en' : 'th');
  return lang;
}

type Dict = Record<string, string>;

const EN: Dict = {
  'menu.subtitle': 'Build your parking empire',
  'menu.new': 'New Game',
  'menu.load': 'Load Game',
  'menu.settings': 'Settings',
  'menu.online': '🌐 Online / Accounts',
  'menu.phase': 'MVP • Parking Tycoon',
  'menu.settingsSoon': 'Settings coming soon',
  'menu.lang': 'ภาษาไทย', // button switches TO Thai when in EN

  'city.title': 'METRO CITY',
  'city.hint': 'Click a district to build or manage a lot',
  'city.share': 'Your market share: {v}%',
  'city.market': '📊 Market',
  'city.build': '+ Build Lot',
  'city.manage': 'Manage ▸',
  'city.land': 'Land: {v}',
  'city.occ': 'Occupancy',
  'city.rep': 'Rep',
  'city.noCash': 'Not enough cash to buy land in {d}',
  'city.exists': '{d} already has a lot',
  'city.goalReached': '🏆 GOAL REACHED — Parking Empire built!',
  'city.goal': '🏁 Goal: value {v} / ฿100M ({p}%)   •   share {s}% / 40%',
  'city.active': '⚡ Active: {v}',

  'lot.back': '◂ Back to City',
  'lot.upgrades': '⚙ Upgrades',
  'lot.expand': '⤢ Expand {w}×{h}',
  'lot.maxSize': 'Max size',
  'lot.price': 'Price',
  'lot.buildTools': 'BUILD TOOLS',
  'lot.staff': 'STAFF',
  'lot.eraser': 'Eraser',
  'lot.free': 'free',
  'lot.noCashBuild': 'Not enough cash!',
  'lot.noCashExpand': 'Not enough cash to expand!',
  'lot.collecting': '💵 Pay lanes: {lanes}   Queue: {q}',
  'lot.noCashier': '⚠ NO cashier — cars leave WITHOUT paying!',
  'lot.noEntry': '⛔ NO entry system — add a cashier or Automatic Barrier or cars can\'t get in!',
  'lot.marketOwned': '✓ Owned',
  'lot.needs': '🔒 needs {v}',
  'lot.buy': 'Buy {v}',

  'hud.save': '💾 Save',
  'hud.menu': '☰ Menu',
  'hud.saved': 'Game saved',
  'hud.saveFail': 'Save unavailable (storage blocked here)',

  'staff.cashier': 'Cashier',
  'staff.security': 'Security',
  'staff.cleaner': 'Cleaner',
  'staff.manager': 'Manager',
  'staff.technician': 'Technician',
};

const TH: Dict = {
  'menu.subtitle': 'สร้างอาณาจักรลานจอดรถของคุณ',
  'menu.new': 'เริ่มเกมใหม่',
  'menu.load': 'โหลดเกม',
  'menu.settings': 'ตั้งค่า',
  'menu.online': '🌐 ออนไลน์ / บัญชี',
  'menu.phase': 'MVP • Parking Tycoon',
  'menu.settingsSoon': 'ตั้งค่า (เร็วๆ นี้)',
  'menu.lang': 'English',

  'city.title': 'เมโทรซิตี้',
  'city.hint': 'คลิกเขตเพื่อสร้างหรือจัดการลานจอด',
  'city.share': 'ส่วนแบ่งตลาดของคุณ: {v}%',
  'city.market': '📊 ตลาด',
  'city.build': '+ สร้างลานจอด',
  'city.manage': 'จัดการ ▸',
  'city.land': 'ค่าที่ดิน: {v}',
  'city.occ': 'ใช้งาน',
  'city.rep': 'ชื่อเสียง',
  'city.noCash': 'เงินไม่พอซื้อที่ดินใน{d}',
  'city.exists': '{d} มีลานจอดแล้ว',
  'city.goalReached': '🏆 บรรลุเป้าหมาย — สร้างอาณาจักรลานจอดสำเร็จ!',
  'city.goal': '🏁 เป้าหมาย: มูลค่า {v} / ฿100M ({p}%)   •   ส่วนแบ่ง {s}% / 40%',
  'city.active': '⚡ เหตุการณ์: {v}',

  'lot.back': '◂ กลับสู่เมือง',
  'lot.upgrades': '⚙ อัปเกรด',
  'lot.expand': '⤢ ขยาย {w}×{h}',
  'lot.maxSize': 'ใหญ่สุดแล้ว',
  'lot.price': 'ราคา',
  'lot.buildTools': 'เครื่องมือก่อสร้าง',
  'lot.staff': 'พนักงาน',
  'lot.eraser': 'ยางลบ',
  'lot.free': 'ฟรี',
  'lot.noCashBuild': 'เงินไม่พอ!',
  'lot.noCashExpand': 'เงินไม่พอขยายลาน!',
  'lot.collecting': '💵 ช่องจ่าย: {lanes}   คิว: {q}',
  'lot.noCashier': '⚠ ไม่มีแคชเชียร์ — รถออกโดยไม่จ่าย!',
  'lot.noEntry': '⛔ ไม่มีระบบทางเข้า — ต้องมีแคชเชียร์หรือไม้กั้นอัตโนมัติ ไม่งั้นรถเข้าไม่ได้!',
  'lot.marketOwned': '✓ มีแล้ว',
  'lot.needs': '🔒 ต้องมี {v}',
  'lot.buy': 'ซื้อ {v}',

  'hud.save': '💾 บันทึก',
  'hud.menu': '☰ เมนู',
  'hud.saved': 'บันทึกเกมแล้ว',
  'hud.saveFail': 'บันทึกไม่ได้ (พื้นที่จัดเก็บถูกบล็อก)',

  'staff.cashier': 'แคชเชียร์',
  'staff.security': 'รปภ.',
  'staff.cleaner': 'พนักงานทำความสะอาด',
  'staff.manager': 'ผู้จัดการ',
  'staff.technician': 'ช่างเทคนิค',
};

const DICTS: Record<Lang, Dict> = { en: EN, th: TH };

export function t(key: string, vars?: Record<string, string | number>): string {
  let s = DICTS[lang][key] ?? EN[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return s;
}

// --- data-object names (by id) ---

const DISTRICT_TH: Dict = {
  downtown: 'ดาวน์ทาวน์',
  office: 'ย่านออฟฟิศ',
  shopping: 'ย่านช้อปปิ้ง',
  residential: 'ย่านที่พักอาศัย',
  hospital: 'ย่านโรงพยาบาล',
};
const BUILDING_TH: Dict = {
  road: 'ถนน',
  parking: 'ช่องจอด',
  entrance: 'ทางเข้า',
  exit: 'ทางออก',
  cashier: 'ตู้แคชเชียร์',
};
const UPGRADE_TH: Dict = {
  automatic_barrier: 'ไม้กั้นอัตโนมัติ',
  qr_payment: 'จ่ายเงินผ่าน QR',
  lpr: 'ระบบอ่านป้ายทะเบียน (LPR)',
  cctv: 'ระบบกล้องวงจรปิด',
  ev_charger: 'ที่ชาร์จรถ EV',
  parking_sensor: 'เซ็นเซอร์อัจฉริยะ',
  dynamic_pricing: 'ราคาแบบไดนามิก',
};

export function tDistrict(id: string, fallback: string): string {
  return lang === 'th' ? DISTRICT_TH[id] ?? fallback : fallback;
}
export function tBuilding(id: string, fallback: string): string {
  return lang === 'th' ? BUILDING_TH[id] ?? fallback : fallback;
}
export function tUpgrade(id: string, fallback: string): string {
  return lang === 'th' ? UPGRADE_TH[id] ?? fallback : fallback;
}
export function tStaff(id: string): string {
  return t(`staff.${id}`);
}
